import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { getLastName } from "../../utils/textUtils";
import { colors } from "../../theme";
import { BRAND_COLORS, brandRgba } from "../../utils/brandAssets";
import { horizontalScroll } from "../home/homeStyles";
import {
  CompareHeadshotRing,
  getPlayerAccent,
  HERO_MULTI_PLAYER_HEADSHOT,
  HERO_TWO_PLAYER_HEADSHOT,
} from "./comparePlayerVisuals";

const STAT_LABEL_MIN_WIDTH = 140;
const PLAYER_COLUMN_MIN = 120;
const CHART_STAT_LABEL_TINT = brandRgba(BRAND_COLORS.cyan, 0.08);
const CHART_STAT_LABEL_BAR = `inset 3px 0 0 ${BRAND_COLORS.cyan}`;

const careerValueColor = (isLeader, playerIndex) =>
  isLeader ? getPlayerAccent(playerIndex) : "text.secondary";

const PlayerIdentity = ({
  slot,
  index,
  headshotSize,
  align = "center",
  sport,
  getName,
  getSubtitle,
}) => {
  const playerId = slot.id || slot.data?.playerId;
  const name = getName(slot.data);
  const subtitle = getSubtitle?.(slot.data);
  const accent = getPlayerAccent(index);
  const textAlign =
    align === "left" ? "left" : align === "right" ? "right" : "center";
  const alignItems =
    align === "left"
      ? "flex-start"
      : align === "right"
        ? "flex-end"
        : "center";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: align === "center" ? "column" : "row",
        alignItems,
        gap: align === "center" ? 1.25 : 2,
        minWidth: 0,
      }}
    >
      {align === "right" && (
        <Box sx={{ textAlign, minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            title={name}
            sx={{ lineHeight: 1.2 }}
          >
            {name}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={subtitle}
              sx={{ display: "block", mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      <CompareHeadshotRing
        playerId={playerId}
        size={headshotSize}
        accent={accent}
        variant="hero"
        sport={sport}
      />

      {align !== "right" && (
        <Box sx={{ textAlign, minWidth: 0, flex: align === "center" ? 0 : 1 }}>
          <Typography
            variant={align === "center" ? "subtitle2" : "subtitle1"}
            fontWeight={700}
            noWrap
            title={name}
            sx={{ lineHeight: 1.2 }}
          >
            {name}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={subtitle}
              sx={{ display: "block", mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const TwoPlayerHero = ({ activePlayers, sport, getName, getSubtitle }) => {
  const left = activePlayers[0];
  const right = activePlayers[1];
  const leftAccent = getPlayerAccent(0);
  const rightAccent = getPlayerAccent(1);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
        gap: { xs: 2, sm: 3 },
        alignItems: "center",
        py: { xs: 2.5, sm: 3 },
        px: { xs: 2, sm: 3 },
        position: "sticky",
        top: 0,
        zIndex: 2,
        bgcolor: colors.paper,
        borderBottom: `1px solid ${colors.border}`,
        backgroundImage: `
          linear-gradient(90deg, ${leftAccent}14 0%, transparent 42%),
          linear-gradient(270deg, ${rightAccent}14 0%, transparent 42%)
        `,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          borderLeft: { sm: `3px solid ${leftAccent}` },
          pl: { sm: 2 },
          py: 1,
        }}
      >
        <PlayerIdentity
          slot={left}
          index={0}
          headshotSize={HERO_TWO_PLAYER_HEADSHOT}
          align="right"
          sport={sport}
          getName={getName}
          getSubtitle={getSubtitle}
        />
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          width: 52,
          height: 52,
          borderRadius: "50%",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255, 255, 255, 0.05)",
          border: `1px solid ${colors.border}`,
          fontWeight: 800,
          fontSize: "0.8125rem",
          letterSpacing: "0.08em",
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        VS
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-start" },
          borderRight: { sm: `3px solid ${rightAccent}` },
          pr: { sm: 2 },
          py: 1,
        }}
      >
        <PlayerIdentity
          slot={right}
          index={1}
          headshotSize={HERO_TWO_PLAYER_HEADSHOT}
          align="left"
          sport={sport}
          getName={getName}
          getSubtitle={getSubtitle}
        />
      </Box>
    </Box>
  );
};

const MultiPlayerHero = ({ activePlayers, sport, getName, getSubtitle }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 2,
      bgcolor: colors.paper,
      borderBottom: `1px solid ${colors.border}`,
      py: 2.5,
      px: 2,
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${activePlayers.length}, minmax(${PLAYER_COLUMN_MIN}px, 1fr))`,
        gap: 2,
        width: "100%",
      }}
    >
      {activePlayers.map((slot, index) => {
        const accent = getPlayerAccent(index);
        return (
          <Box
            key={slot.id || slot.data?.playerId}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1.5,
              borderRadius: 2,
              borderTop: `3px solid ${accent}`,
              background: `linear-gradient(180deg, ${accent}18 0%, transparent 100%)`,
            }}
          >
            <PlayerIdentity
              slot={slot}
              index={index}
              headshotSize={HERO_MULTI_PLAYER_HEADSHOT}
              align="center"
              sport={sport}
              getName={getName}
              getSubtitle={getSubtitle}
            />
          </Box>
        );
      })}
    </Box>
  </Box>
);

const CompareCareerTable = ({
  playerSlots = [],
  sport = "mlb",
  showComparison = false,
  getName,
  getSubtitle,
  statRows = [],
  getRawValue,
  formatValue,
  isHighlightedRow,
  getLeaderIndex,
}) => {
  const activePlayers = playerSlots.filter((slot) => slot.data);

  if (!showComparison || activePlayers.length < 2) {
    return null;
  }

  const isTwoPlayer = activePlayers.length === 2;
  const useHorizontalScroll = activePlayers.length >= 4;
  const playerCount = activePlayers.length;

  const tableMinWidth = useHorizontalScroll
    ? STAT_LABEL_MIN_WIDTH + playerCount * PLAYER_COLUMN_MIN
    : undefined;

  const headerCellSx = {
    py: 1.25,
    fontWeight: 600,
    fontSize: "0.75rem",
    borderBottom: `1px solid ${colors.border}`,
  };

  const statGrid = (
    <Box
      component="table"
      sx={{
        width: "100%",
        minWidth: tableMinWidth,
        borderCollapse: "collapse",
        tableLayout: "fixed",
      }}
    >
      <Box component="colgroup">
        <Box component="col" sx={{ width: STAT_LABEL_MIN_WIDTH }} />
        {activePlayers.map((slot) => (
          <Box
            key={`col-${slot.id || slot.data.playerId}`}
            component="col"
            sx={{ width: useHorizontalScroll ? PLAYER_COLUMN_MIN : undefined }}
          />
        ))}
      </Box>
      <Box component="thead">
        <Box component="tr">
          <Box
            component="th"
            scope="col"
            sx={{
              ...headerCellSx,
              px: 2,
              textAlign: "left",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
              position: "sticky",
              left: 0,
              bgcolor: colors.paper,
              zIndex: 1,
            }}
          >
            Stat
          </Box>
          {activePlayers.map((slot, index) => (
            <Box
              key={`header-${slot.id || slot.data.playerId}`}
              component="th"
              scope="col"
              sx={{
                ...headerCellSx,
                px: 1,
                textAlign: "center",
                color: getPlayerAccent(index),
              }}
            >
              {getLastName(getName(slot.data))}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {statRows.map((row) => {
          const rawValues = activePlayers.map((slot) =>
            getRawValue(slot.data, row)
          );
          const leaderIndex = getLeaderIndex?.(rawValues, row) ?? -1;
          const isHighlighted = Boolean(isHighlightedRow?.(row));
          const rowBorder = `1px solid ${colors.border}`;

          return (
            <Box component="tr" key={row.key || row.id}>
              <Box
                component="th"
                scope="row"
                sx={{
                  py: 1.25,
                  px: 2,
                  fontWeight: isHighlighted ? 600 : 500,
                  fontSize: "0.8125rem",
                  color: isHighlighted ? "primary.main" : "text.secondary",
                  bgcolor: isHighlighted ? CHART_STAT_LABEL_TINT : colors.paper,
                  boxShadow: isHighlighted ? CHART_STAT_LABEL_BAR : undefined,
                  borderBottom: rowBorder,
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  textAlign: "left",
                }}
              >
                {row.label}
              </Box>
              {rawValues.map((value, index) => {
                const isLeader = leaderIndex === index;
                return (
                  <Box
                    key={`${row.key || row.id}-${index}`}
                    component="td"
                    sx={{
                      py: 1.25,
                      px: 1.5,
                      textAlign: "center",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: isLeader ? 700 : 400,
                      fontSize: "0.9375rem",
                      color: careerValueColor(isLeader, index),
                      bgcolor: "transparent",
                      borderBottom: rowBorder,
                    }}
                  >
                    {formatValue(value, row.format)}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Paper sx={{ p: 0, mt: 2, overflow: "hidden", width: "100%" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Career totals
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Head-to-head career numbers — chart stat marked on the Stat label, leaders in each player's color
        </Typography>
      </Box>

      {isTwoPlayer ? (
        <TwoPlayerHero
          activePlayers={activePlayers}
          sport={sport}
          getName={getName}
          getSubtitle={getSubtitle}
        />
      ) : (
        <MultiPlayerHero
          activePlayers={activePlayers}
          sport={sport}
          getName={getName}
          getSubtitle={getSubtitle}
        />
      )}

      <Box
        sx={{
          ...(useHorizontalScroll ? horizontalScroll : {}),
          px: 2,
          pb: 2,
          width: "100%",
        }}
      >
        {statGrid}
      </Box>
    </Paper>
  );
};

export default CompareCareerTable;
